import { useEffect, useRef, useCallback } from 'react';
import { Canvas, Rect, Text, Group, Line, Path, Point } from 'fabric';
import { ParsedGraph } from '../../types/ontology';
import { calculateLayout, LayoutNode, LayoutEdge } from '../../utils/layoutEngine';
import './OntologyCanvas.css';

interface OntologyCanvasProps {
    graph: ParsedGraph | null;
}

// Color scheme
const COLORS = {
    class: {
        fill: '#2d2418',
        stroke: '#ff6b2b',
        text: '#e8d5b7',
        glow: 'rgba(255, 107, 43, 0.3)',
    },
    objectProperty: {
        stroke: '#3dd6d0',
        text: '#3dd6d0',
    },
    datatypeProperty: {
        stroke: '#ffb347',
        text: '#ffb347',
    },
    subClassOf: {
        stroke: '#c45a2c',
    },
    grid: 'rgba(61, 214, 208, 0.05)',
    gridMajor: 'rgba(61, 214, 208, 0.1)',
};

const NODE_WIDTH = 160;
const NODE_HEIGHT = 60;
const GRID_SIZE = 20;

function OntologyCanvas({ graph }: OntologyCanvasProps) {
    const canvasContainerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const minimapRef = useRef<HTMLCanvasElement>(null);
    const fabricRef = useRef<Canvas | null>(null);
    const isPanningRef = useRef(false);
    const lastPosRef = useRef({ x: 0, y: 0 });

    // Track nodes and edges for dynamic updates
    const nodesMapRef = useRef<Map<string, { group: Group; node: LayoutNode }>>(new Map());
    const edgesMapRef = useRef<Map<string, { paths: (Path | Line)[]; labels: Text[]; edge: LayoutEdge }>>(new Map());

    // Initialize Fabric canvas
    useEffect(() => {
        if (!canvasRef.current || !canvasContainerRef.current) return;

        const container = canvasContainerRef.current;
        const canvas = new Canvas(canvasRef.current, {
            width: container.clientWidth,
            height: container.clientHeight,
            backgroundColor: '#1a1410',
            selection: true,
            preserveObjectStacking: true,
        });

        fabricRef.current = canvas;

        // Draw grid
        drawGrid(canvas, container.clientWidth, container.clientHeight);

        // Pan with mouse drag on empty space
        canvas.on('mouse:down', (opt) => {
            if (!opt.target) {
                isPanningRef.current = true;
                const evt = opt.e as MouseEvent;
                lastPosRef.current = { x: evt.clientX, y: evt.clientY };
                canvas.selection = false;
            }
        });

        canvas.on('mouse:move', (opt) => {
            if (isPanningRef.current) {
                const evt = opt.e as MouseEvent;
                const vpt = canvas.viewportTransform;
                if (vpt) {
                    vpt[4] += evt.clientX - lastPosRef.current.x;
                    vpt[5] += evt.clientY - lastPosRef.current.y;
                    canvas.requestRenderAll();
                    lastPosRef.current = { x: evt.clientX, y: evt.clientY };
                }
            }
        });

        canvas.on('mouse:up', () => {
            isPanningRef.current = false;
            canvas.selection = true;
        });

        // Handle node movement - recalculate connected edges
        canvas.on('object:moving', (opt) => {
            if (opt.target && (opt.target as any).data?.type === 'class') {
                const nodeId = (opt.target as any).data.id;
                updateConnectedEdges(canvas, nodeId);
                canvas.requestRenderAll();
            }
        });

        // Snap to grid on drop
        canvas.on('object:modified', (opt) => {
            if (opt.target && (opt.target as any).data?.type === 'class') {
                const left = opt.target.left || 0;
                const top = opt.target.top || 0;
                const snappedLeft = snapToGrid(left);
                const snappedTop = snapToGrid(top);
                opt.target.set({ left: snappedLeft, top: snappedTop });

                // Update node data
                const nodeId = (opt.target as any).data.id;
                const nodeData = nodesMapRef.current.get(nodeId);
                if (nodeData) {
                    nodeData.node.x = snappedLeft;
                    nodeData.node.y = snappedTop;
                }

                updateConnectedEdges(canvas, nodeId);
                canvas.requestRenderAll();
            }
        });

        // Handle double-click on canvas level (not on groups)
        canvas.on('mouse:dblclick', (opt) => {
            if (opt.target && (opt.target as any).data?.type === 'class') {
                const nodeId = (opt.target as any).data.id;
                const nodeData = nodesMapRef.current.get(nodeId);
                if (nodeData) {
                    const newLabel = prompt('Edit class name:', nodeData.node.label);
                    if (newLabel && newLabel !== nodeData.node.label) {
                        nodeData.node.label = newLabel;
                        // Update the text in the group
                        const items = (opt.target as Group)._objects || [];
                        items.forEach(obj => {
                            if (obj instanceof Text && obj.fontSize === 14) {
                                obj.set('text', newLabel);
                            }
                        });
                        canvas.requestRenderAll();
                    }
                }
            }
        });

        // Zoom with mouse wheel
        canvas.on('mouse:wheel', (opt) => {
            const delta = opt.e.deltaY;
            let zoom = canvas.getZoom();
            zoom *= 0.999 ** delta;
            zoom = Math.min(Math.max(0.2, zoom), 3);
            canvas.zoomToPoint(new Point(opt.e.offsetX, opt.e.offsetY), zoom);
            opt.e.preventDefault();
            opt.e.stopPropagation();
        });

        // Handle resize
        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const { width, height } = entry.contentRect;
                canvas.setDimensions({ width, height });
                canvas.requestRenderAll();
            }
        });
        resizeObserver.observe(container);

        return () => {
            resizeObserver.disconnect();
            canvas.dispose();
        };
    }, []);

    // Update canvas when graph changes
    useEffect(() => {
        if (!fabricRef.current || !graph) return;

        const canvas = fabricRef.current;

        // Clear existing objects (except grid) and refs
        const objects = canvas.getObjects();
        objects.forEach(obj => {
            if (!(obj as any).data?.isGrid) {
                canvas.remove(obj);
            }
        });
        nodesMapRef.current.clear();
        edgesMapRef.current.clear();

        // Calculate layout
        const layout = calculateLayout(graph);

        // Draw edges first (so they're behind nodes)
        layout.edges.forEach(edge => {
            drawEdge(canvas, edge, edgesMapRef);
        });

        // Draw nodes
        layout.nodes.forEach(node => {
            drawNode(canvas, node, nodesMapRef, edgesMapRef);
        });

        canvas.requestRenderAll();
        updateMinimap();
    }, [graph]);

    const updateMinimap = useCallback(() => {
        if (!minimapRef.current || !fabricRef.current) return;

        const canvas = fabricRef.current;
        const ctx = minimapRef.current.getContext('2d');
        if (!ctx) return;

        const scale = 0.1;
        ctx.clearRect(0, 0, 150, 100);
        ctx.fillStyle = '#1a1410';
        ctx.fillRect(0, 0, 150, 100);

        // Draw minimap content
        const objects = canvas.getObjects();
        objects.forEach(obj => {
            if ((obj as any).data?.isGrid) return;

            const left = (obj.left || 0) * scale;
            const top = (obj.top || 0) * scale;

            if ((obj as any).data?.type === 'class') {
                ctx.fillStyle = COLORS.class.stroke;
                ctx.fillRect(left, top, NODE_WIDTH * scale, NODE_HEIGHT * scale);
            }
        });

        // Draw viewport rectangle
        const vpt = canvas.viewportTransform;
        if (vpt) {
            const zoom = canvas.getZoom();
            ctx.strokeStyle = '#3dd6d0';
            ctx.lineWidth = 1;
            ctx.strokeRect(
                -vpt[4] * scale / zoom,
                -vpt[5] * scale / zoom,
                canvas.width! * scale / zoom,
                canvas.height! * scale / zoom
            );
        }
    }, []);

    return (
        <div className="ontology-canvas" ref={canvasContainerRef}>
            <canvas ref={canvasRef} />
            <div className="minimap">
                <canvas ref={minimapRef} width={150} height={100} />
            </div>
            {!graph && (
                <div className="canvas-empty">
                    <span className="canvas-empty__icon">◈</span>
                    <span>Write valid Turtle to see visualization</span>
                </div>
            )}
        </div>
    );
}

function drawGrid(canvas: Canvas, width: number, height: number) {
    const gridGroup: Line[] = [];

    // Minor grid lines
    for (let i = 0; i <= width; i += GRID_SIZE) {
        const line = new Line([i, 0, i, height], {
            stroke: COLORS.grid,
            strokeWidth: 1,
            selectable: false,
            evented: false,
        });
        (line as any).data = { isGrid: true };
        gridGroup.push(line);
    }

    for (let i = 0; i <= height; i += GRID_SIZE) {
        const line = new Line([0, i, width, i], {
            stroke: COLORS.grid,
            strokeWidth: 1,
            selectable: false,
            evented: false,
        });
        (line as any).data = { isGrid: true };
        gridGroup.push(line);
    }

    // Major grid lines
    for (let i = 0; i <= width; i += GRID_SIZE * 5) {
        const line = new Line([i, 0, i, height], {
            stroke: COLORS.gridMajor,
            strokeWidth: 1,
            selectable: false,
            evented: false,
        });
        (line as any).data = { isGrid: true };
        gridGroup.push(line);
    }

    for (let i = 0; i <= height; i += GRID_SIZE * 5) {
        const line = new Line([0, i, width, i], {
            stroke: COLORS.gridMajor,
            strokeWidth: 1,
            selectable: false,
            evented: false,
        });
        (line as any).data = { isGrid: true };
        gridGroup.push(line);
    }

    gridGroup.forEach(line => canvas.add(line));
}

function snapToGrid(value: number): number {
    return Math.round(value / GRID_SIZE) * GRID_SIZE;
}

function updateConnectedEdges(canvas: Canvas, nodeId: string) {
    const nodesMapRef = (canvas as any).__nodesMapRef;
    const edgesMapRef = (canvas as any).__edgesMapRef;
    if (!nodesMapRef || !edgesMapRef) return;

    const nodeData = nodesMapRef.get(nodeId);
    if (!nodeData) return;

    // Update node position from group
    const group = nodeData.group;
    nodeData.node.x = group.left || 0;
    nodeData.node.y = group.top || 0;

    // Update all connected edges
    edgesMapRef.forEach((edgeData: any) => {
        const { edge, paths, labels } = edgeData;

        if (edge.source === nodeId || edge.target === nodeId) {
            // Find source and target nodes
            const sourceData = nodesMapRef.get(edge.source);
            const targetData = nodesMapRef.get(edge.target);

            if (!sourceData || !targetData) return;

            // Recalculate path
            const newPath = calculatePCBPath(sourceData.node, targetData.node,
                Array.from(nodesMapRef.values()).map((n: any) => n.node));
            edge.path = newPath;

            // Update path objects
            const color = edge.type === 'subClassOf'
                ? COLORS.subClassOf.stroke
                : edge.type === 'domain' || edge.type === 'range'
                    ? COLORS.objectProperty.stroke
                    : COLORS.datatypeProperty.stroke;

            // UPDATE existing paths instead of removing them to avoid ghost edges

            // Build the new path string
            let pathString = `M ${newPath[0].x} ${newPath[0].y}`;
            for (let i = 1; i < newPath.length; i++) {
                const prev = newPath[i - 1];
                const curr = newPath[i];
                const next = newPath[i + 1];

                if (next) {
                    const radius = 8;
                    const dx1 = curr.x - prev.x;
                    const dy1 = curr.y - prev.y;
                    const dx2 = next.x - curr.x;
                    const dy2 = next.y - curr.y;

                    if ((dx1 !== 0 && dy2 !== 0) || (dy1 !== 0 && dx2 !== 0)) {
                        const corner1X = curr.x - Math.sign(dx1) * radius;
                        const corner1Y = curr.y - Math.sign(dy1) * radius;
                        const corner2X = curr.x + Math.sign(dx2) * radius;
                        const corner2Y = curr.y + Math.sign(dy2) * radius;

                        pathString += ` L ${corner1X} ${corner1Y}`;
                        pathString += ` Q ${curr.x} ${curr.y} ${corner2X} ${corner2Y}`;
                    } else {
                        pathString += ` L ${curr.x} ${curr.y}`;
                    }
                } else {
                    pathString += ` L ${curr.x} ${curr.y}`;
                }
            }

            // Calculate arrow
            const lastPoint = newPath[newPath.length - 1];
            const prevPoint = newPath[newPath.length - 2];
            const angle = Math.atan2(lastPoint.y - prevPoint.y, lastPoint.x - prevPoint.x);
            const arrowSize = 10;
            const arrowString = `M ${lastPoint.x} ${lastPoint.y} 
                 L ${lastPoint.x - arrowSize * Math.cos(angle - Math.PI / 6)} ${lastPoint.y - arrowSize * Math.sin(angle - Math.PI / 6)}
                 M ${lastPoint.x} ${lastPoint.y}
                 L ${lastPoint.x - arrowSize * Math.cos(angle + Math.PI / 6)} ${lastPoint.y - arrowSize * Math.sin(angle + Math.PI / 6)}`;

            // Update or create paths
            if (paths.length >= 2) {
                // UPDATE existing paths
                paths[0].set({ path: pathString, stroke: color });
                paths[1].set({ path: arrowString, stroke: color });
            } else {
                // First time - create new paths
                const line = new Path(pathString, {
                    stroke: color,
                    strokeWidth: 2,
                    fill: '',
                    selectable: false,
                    evented: false,
                });
                canvas.add(line);
                (line as any).sendToBack();
                paths.push(line);

                const arrow = new Path(arrowString, {
                    stroke: color,
                    strokeWidth: 2,
                    fill: '',
                    selectable: false,
                    evented: false,
                });
                canvas.add(arrow);
                (arrow as any).sendToBack();
                paths.push(arrow);
            }

            // Update or create label
            if (edge.label) {
                const midIndex = Math.floor(newPath.length / 2);
                const midPoint = newPath[midIndex];

                if (labels.length > 0) {
                    // UPDATE existing label
                    labels[0].set({
                        left: midPoint.x,
                        top: midPoint.y - 12,
                        fill: color
                    });
                } else {
                    // Create new label
                    const labelText = new Text(edge.label, {
                        left: midPoint.x,
                        top: midPoint.y - 12,
                        fontSize: 10,
                        fontFamily: 'Share Tech Mono',
                        fill: color,
                        originX: 'center',
                        backgroundColor: '#1a1410',
                        selectable: false,
                    });
                    canvas.add(labelText);
                    labels.push(labelText);
                }
            }
        }
    });
}

function calculatePCBPath(
    source: LayoutNode,
    target: LayoutNode,
    _allNodes: LayoutNode[]
): { x: number; y: number }[] {
    // Calculate center points of both nodes
    const sourceCenterX = source.x + source.width / 2;
    const sourceCenterY = source.y + source.height / 2;
    const targetCenterX = target.x + target.width / 2;
    const targetCenterY = target.y + target.height / 2;

    // Determine relative position
    const dx = targetCenterX - sourceCenterX;
    const dy = targetCenterY - sourceCenterY;

    // Define connection points for each side
    const sourcePoints = {
        top: { x: sourceCenterX, y: source.y },
        right: { x: source.x + source.width, y: sourceCenterY },
        bottom: { x: sourceCenterX, y: source.y + source.height },
        left: { x: source.x, y: sourceCenterY }
    };

    const targetPoints = {
        top: { x: targetCenterX, y: target.y },
        right: { x: target.x + target.width, y: targetCenterY },
        bottom: { x: targetCenterX, y: target.y + target.height },
        left: { x: target.x, y: targetCenterY }
    };

    // Choose optimal exit and entry points based on relative position
    let sourcePoint: { x: number; y: number };
    let targetPoint: { x: number; y: number };

    // Determine exit point from source
    if (Math.abs(dx) > Math.abs(dy)) {
        // Horizontal dominance
        sourcePoint = dx > 0 ? sourcePoints.right : sourcePoints.left;
    } else {
        // Vertical dominance
        sourcePoint = dy > 0 ? sourcePoints.bottom : sourcePoints.top;
    }

    // Determine entry point to target (opposite of source direction)
    if (Math.abs(dx) > Math.abs(dy)) {
        // Horizontal dominance
        targetPoint = dx > 0 ? targetPoints.left : targetPoints.right;
    } else {
        // Vertical dominance
        targetPoint = dy > 0 ? targetPoints.top : targetPoints.bottom;
    }

    const path: { x: number; y: number }[] = [];
    path.push(sourcePoint);

    // Create orthogonal path with one or two intermediate points
    const useMidHorizontal = Math.abs(dx) > Math.abs(dy);

    if (useMidHorizontal) {
        // Horizontal first, then vertical
        const midX = (sourcePoint.x + targetPoint.x) / 2;

        if (Math.abs(sourcePoint.y - targetPoint.y) > 5) {
            path.push({ x: midX, y: sourcePoint.y });
            path.push({ x: midX, y: targetPoint.y });
        }
    } else {
        // Vertical first, then horizontal
        const midY = (sourcePoint.y + targetPoint.y) / 2;

        if (Math.abs(sourcePoint.x - targetPoint.x) > 5) {
            path.push({ x: sourcePoint.x, y: midY });
            path.push({ x: targetPoint.x, y: midY });
        }
    }

    path.push(targetPoint);

    return path;
}

function drawNode(
    canvas: Canvas,
    node: LayoutNode,
    nodesMapRef: React.MutableRefObject<Map<string, { group: Group; node: LayoutNode }>>,
    edgesMapRef: React.MutableRefObject<Map<string, { paths: (Path | Line)[]; labels: Text[]; edge: LayoutEdge }>>
) {
    const { x, y, label, uri, type } = node;

    // Glow effect (shadow rect)
    const glow = new Rect({
        left: x - 3,
        top: y - 3,
        width: NODE_WIDTH + 6,
        height: NODE_HEIGHT + 6,
        fill: 'transparent',
        stroke: COLORS.class.glow,
        strokeWidth: 6,
        rx: 8,
        ry: 8,
        selectable: false,
        evented: false,
    });

    // Main rectangle
    const rect = new Rect({
        left: x,
        top: y,
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        fill: COLORS.class.fill,
        stroke: COLORS.class.stroke,
        strokeWidth: 2,
        rx: 5,
        ry: 5,
    });

    // Label text
    const text = new Text(label, {
        left: x + NODE_WIDTH / 2,
        top: y + NODE_HEIGHT / 2,
        fontSize: 14,
        fontFamily: 'Share Tech Mono',
        fill: COLORS.class.text,
        originX: 'center',
        originY: 'center',
        selectable: false,
    });

    // Type indicator
    const typeText = new Text(type.toUpperCase(), {
        left: x + 8,
        top: y + 6,
        fontSize: 9,
        fontFamily: 'Share Tech Mono',
        fill: COLORS.class.stroke,
        selectable: false,
    });

    const group = new Group([glow, rect, text, typeText], {
        left: x,
        top: y,
        selectable: true,
        hasControls: false,
        hasBorders: true,
        borderColor: COLORS.class.stroke,
        borderScaleFactor: 2,
    });

    (group as any).data = { type: 'class', uri, id: node.id };

    // Store reference
    nodesMapRef.current.set(node.id, { group, node });

    // Store refs on canvas for access in event handlers
    (canvas as any).__nodesMapRef = nodesMapRef.current;
    (canvas as any).__edgesMapRef = edgesMapRef.current;

    canvas.add(group);
}

function drawEdge(
    canvas: Canvas,
    edge: LayoutEdge,
    edgesMapRef: React.MutableRefObject<Map<string, { paths: (Path | Line)[]; labels: Text[]; edge: LayoutEdge }>>
) {
    const { path, type, label } = edge;

    if (path.length < 2) return;

    const color = type === 'subClassOf'
        ? COLORS.subClassOf.stroke
        : type === 'domain' || type === 'range'
            ? COLORS.objectProperty.stroke
            : COLORS.datatypeProperty.stroke;

    // Build PCB-style path (orthogonal with rounded corners)
    let pathString = `M ${path[0].x} ${path[0].y}`;

    for (let i = 1; i < path.length; i++) {
        const prev = path[i - 1];
        const curr = path[i];
        const next = path[i + 1];

        if (next) {
            // Add rounded corner
            const radius = 8;
            const dx1 = curr.x - prev.x;
            const dy1 = curr.y - prev.y;
            const dx2 = next.x - curr.x;
            const dy2 = next.y - curr.y;

            // Only round if direction changes
            if ((dx1 !== 0 && dy2 !== 0) || (dy1 !== 0 && dx2 !== 0)) {
                const corner1X = curr.x - Math.sign(dx1) * radius;
                const corner1Y = curr.y - Math.sign(dy1) * radius;
                const corner2X = curr.x + Math.sign(dx2) * radius;
                const corner2Y = curr.y + Math.sign(dy2) * radius;

                pathString += ` L ${corner1X} ${corner1Y}`;
                pathString += ` Q ${curr.x} ${curr.y} ${corner2X} ${corner2Y}`;
            } else {
                pathString += ` L ${curr.x} ${curr.y}`;
            }
        } else {
            pathString += ` L ${curr.x} ${curr.y}`;
        }
    }

    const line = new Path(pathString, {
        stroke: color,
        strokeWidth: 2,
        fill: '',
        selectable: false,
        evented: false,
    });

    canvas.add(line);
    const paths: (Path | Line)[] = [line];
    const labels: Text[] = [];

    // Draw arrow at end
    const lastPoint = path[path.length - 1];
    const prevPoint = path[path.length - 2];
    const angle = Math.atan2(lastPoint.y - prevPoint.y, lastPoint.x - prevPoint.x);

    const arrowSize = 10;
    const arrow = new Path(
        `M ${lastPoint.x} ${lastPoint.y} 
     L ${lastPoint.x - arrowSize * Math.cos(angle - Math.PI / 6)} ${lastPoint.y - arrowSize * Math.sin(angle - Math.PI / 6)}
     M ${lastPoint.x} ${lastPoint.y}
     L ${lastPoint.x - arrowSize * Math.cos(angle + Math.PI / 6)} ${lastPoint.y - arrowSize * Math.sin(angle + Math.PI / 6)}`,
        {
            stroke: color,
            strokeWidth: 2,
            fill: '',
            selectable: false,
            evented: false,
        }
    );

    canvas.add(arrow);
    paths.push(arrow);

    // Draw label - always show if available
    if (label) {
        const midIndex = Math.floor(path.length / 2);
        const midPoint = path[midIndex];

        const labelText = new Text(label, {
            left: midPoint.x,
            top: midPoint.y - 12,
            fontSize: 10,
            fontFamily: 'Share Tech Mono',
            fill: color,
            originX: 'center',
            backgroundColor: '#1a1410',
            selectable: false,
        });

        canvas.add(labelText);
        labels.push(labelText);
    }

    // Store edge reference for dynamic updates
    edgesMapRef.current.set(edge.id, { paths, labels, edge });
}

export default OntologyCanvas;
